/**
 * Translates between PCQM's quote shape (`QuoteV2DTO`) and the shape this app's store
 * expects, for quote endpoints that have moved to the `/api/cst` pass-through.
 *
 * The C# layer used to do this via QuoteConverter. Almost all of that converter was a
 * type bridge between two mirror hierarchies; only the handful of differences below are
 * real. Same idea as `normalizeAccessory` in DealerAccessory.api.js, which the accessory
 * catalog migration introduced for exactly this reason.
 *
 * Deliberately NOT translated:
 *   ParsedStatus, QuoteDetails.Amount, LatestSavedVwpfsPrivateLeaseCall -- nothing reads them.
 *   Errors -- via GetQuoteById C# only ever sent an empty envelope, and its two readers
 *   (quoteOrderService) v-for over `Errors?.Messages`, which renders nothing either way.
 *
 * Keep this shared: every quote call that moves to the pass-through (CST-4514) must route its
 * response through normalizeQuote, or the store would hold two different shapes depending on how
 * the user arrived at the quote.
 */

// PCQM's accessory Category spelling differs from constants.PRODUCT_CATEGORIES.
// Mirrors CATEGORY_ALIASES in DealerAccessory.api.js.
const CATEGORY_ALIASES = {
  DealerAccessoires: 'DealerAccessories',
}
const CATEGORY_ALIASES_OUT = Object.fromEntries(
  Object.entries(CATEGORY_ALIASES).map(([pcqm, app]) => [app, pcqm])
)

// PCQM writes Bpm, the app reads BPM. Every other price key already matches.
function normalizePrice(price) {
  if (!price) return price
  return { ...price, BPM: price.Bpm }
}

function denormalizePrice(price) {
  if (!price) return price
  const { BPM, ...rest } = price
  return { ...rest, Bpm: BPM !== undefined ? BPM : price.Bpm }
}

function normalizeCar(car) {
  if (!car) return car
  return { ...car, KomNumber: car.KOMNumber }
}

function denormalizeCar(car) {
  if (!car) return car
  const { KomNumber, ...rest } = car
  return { ...rest, KOMNumber: KomNumber !== undefined ? KomNumber : car.KOMNumber }
}

// Most of what follows exists because C# deserialised PCQM's JSON into typed classes with Newtonsoft
// (case-insensitive) and serialised it back. That silently renamed keys to the C# property casing,
// filled missing value-type fields with 0/false, and coerced "false" to false. Only the fields the
// app actually reads are mirrored here -- see the CST-4514 step 1 diff in the progress doc.

function normalizeAccessory(accessory) {
  if (!accessory) return accessory
  return {
    ...accessory,
    Category: CATEGORY_ALIASES[accessory.Category] || accessory.Category,
    Price: normalizePrice(accessory.Price),
    // PCQM sends the string "false", which is truthy (Settle.vue, SelectedProducts.vue).
    // Same fix as DealerAccessory.api.js.
    CustomPurchasePrice:
      accessory.CustomPurchasePrice === true || accessory.CustomPurchasePrice === 'true',
  }
}

// PCQM: ID/ShortID. The store matches and removes control codes on Id (controlCodes.js,
// quote.js removeFromControlCodes). purchaseOrderService.js maps the same thing for purchase orders.
function normalizeControlCode(controlCode) {
  if (!controlCode) return controlCode
  return {
    ...controlCode,
    Id: controlCode.Id ?? controlCode.ID,
    ShortId: controlCode.ShortId ?? controlCode.ShortID,
  }
}

// PCQM: HouseNumber. FleetContractDetails.vue reads Housenumber.
function normalizeFleetSalesContract(contract) {
  const company = contract?.CustomerFleetCompany
  if (!company) return contract
  return {
    ...contract,
    CustomerFleetCompany: { ...company, Housenumber: company.Housenumber ?? company.HouseNumber },
  }
}

// The numeric properties of C#'s LatestVwpfsFinancingResult: C# made them 0 when PCQM left them out,
// which it does when the last VWPFS call failed. FinancingBlock.vue loads them into its inputs.
const VWPFS_FINANCING_NUMBERS = [
  'Duration',
  'DownPayment',
  'BalloonPayment',
  'InterestRateEffective',
  'InterestRateNominal',
  'MonthPrice',
  'NetCreditAmount',
  'GrossCreditAmount',
  'ObjectPriceExVatExBpm',
  'ObjectPriceInclVatInclBpm',
  'FinalRate',
]

// PCQM stores Errors/Warnings as JSON text, including the text "null". C#'s JsonStringConverter
// parsed it into an object (or null).
function parseJsonText(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeLatestVwpfsCall(call) {
  if (!call) return call
  const numbers = Object.fromEntries(VWPFS_FINANCING_NUMBERS.map((key) => [key, call[key] ?? 0]))
  return {
    ...call,
    ...numbers,
    Warnings: parseJsonText(call.Warnings),
    Errors: parseJsonText(call.Errors),
  }
}

// C# ChildQuote.FormattedStatus, which AdditionalOrderBlock.vue displays. PCQM only sends Status.
const CHILD_QUOTE_STATUSES = {
  childquoteconcept: 'CONCEPT',
  childquote: 'QUOTE',
  childquotesalesorder: 'SALESORDER',
}

function normalizeChildQuote(childQuote) {
  if (!childQuote) return childQuote
  return {
    ...childQuote,
    FormattedStatus:
      childQuote.FormattedStatus ??
      CHILD_QUOTE_STATUSES[String(childQuote.Status ?? '').toLowerCase()] ??
      'UNKNOWN',
  }
}

function denormalizeAccessory(accessory) {
  if (!accessory) return accessory
  return {
    ...accessory,
    Category: CATEGORY_ALIASES_OUT[accessory.Category] || accessory.Category,
    Price: denormalizePrice(accessory.Price),
  }
}

// PCQM emits DeliveryDates in camelCase (expectedDeliveryDateCustomer); every consumer
// reads PascalCase (ExpectedDeliveryDateCustomer) -- ExpectedDeliveryDatePopup,
// ExpectedDeliveryDatePrompt, DoNotDeliverBefore, ExpectedDeliveryDate and
// mixins/checkExpectedDeliveryDate. Capitalising the initial is deliberately generic so a
// field added on the PCQM side does not need another entry here.
//
// Do NOT "fix" this by renaming the fields on PCQM's DeliveryDatesResponseDTO. That DTO is
// shared by four consumers: the CST quote, the CST quote list, Pon purchase orders, and the
// /quoteinfo BI endpoint -- and BIQuoteInfoDTO annotates its whole payload camelCase on
// purpose. Renaming would silently change the BI contract for an external consumer.
//
// The clean end state is the opposite flip: rename these five Vue consumers to camelCase and
// drop this function. That is only safe once every writer of state.quote.DeliveryDates has
// migrated off C# -- update, previewSave and the create calls still serve it PascalCase (CST-4514).
function pascalCaseKeys(o) {
  if (!o) return o
  return Object.fromEntries(
    Object.entries(o).map(([k, v]) => [k.charAt(0).toUpperCase() + k.slice(1), v])
  )
}

// PCQM does not emit PrecalculativeResultGuid; QuoteConverter computed it. It is not an id --
// the margin is spliced into a random GUID so it is not sitting on the page as a plain number.
// ConfigurationBaseCar.vue renders it whenever the dealer settings do not hide the VCR, i.e.
// for most dealers.
//
// Since CST-4514 getquotebyid goes through the pass-through too, so this is now the only source
// of the value on a reloaded quote.
//
// C#: guid[0..13] + (+|-)MarginExcl + '-' + MarginPercentage + guid[19..36], or '' when either
// margin is absent -- PCQM strips both for a dealer that hides the VCR or that it cannot identify.
function randomGuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function precalculativeResultGuid(totalPrice) {
  const marginExcl = totalPrice?.MarginExcl
  const marginPercentage = totalPrice?.MarginPercentage
  if (
    marginExcl == null ||
    marginExcl === '' ||
    marginPercentage == null ||
    marginPercentage === ''
  )
    return ''

  const margin = String(marginExcl).startsWith('-') ? String(marginExcl) : `+${marginExcl}`
  const guid = randomGuid()
  return `${guid.substring(0, 13)}${margin}-${marginPercentage}${guid.substring(19, 36)}`
}

// C# sorted these; PCQM returns an unordered Set, so the app has to impose the order.
function byCreatedOn(a, b) {
  return new Date(a?.CreatedOn ?? 0) - new Date(b?.CreatedOn ?? 0)
}

function normalizeQuoteDetails(details) {
  if (!details) return details
  return {
    ...details,
    // PCQM: StockOrder. Store getter reads QuoteDetails.IsStockOrder.
    IsStockOrder: details.StockOrder,
    Cars: details.Cars?.map(normalizeCar),
    DealerAccessories: details.DealerAccessories?.map(normalizeAccessory),
    ControlCodes: details.ControlCodes?.map(normalizeControlCode),
    FleetSalesContract: normalizeFleetSalesContract(details.FleetSalesContract),
    // C# defaulted these when PCQM sent null; keep that so `v-if` checks behave the same.
    ShowVwpfsFinancePdf: details.ShowVwpfsFinancePdf ?? true,
    ShowVwpfsPrivateLeasePdf: details.ShowVwpfsPrivateLeasePdf ?? true,
    ShowVwpfsOperationalLeasePdf: details.ShowVwpfsOperationalLeasePdf ?? false,
    BpmCalculated: details.BpmCalculated ?? true,
    CustomerType: details.CustomerType ?? {},
  }
}

/**
 * C# answered a failed quote call with `{ Messages: [text] }`; PCQM answers `{ Code, Message: text }`
 * with the same text. quoteOrderService shows `e.response.data.Messages`, so wrap it.
 */
export function withErrorMessages(error) {
  const data = error?.response?.data
  if (data && typeof data === 'object' && !Array.isArray(data.Messages) && data.Message) {
    error.response.data = { ...data, Messages: [data.Message] }
  }
  return error
}

/** PCQM `QuoteV2DTO` -> the shape the store expects. */
export function normalizeQuote(quote) {
  if (!quote) return quote

  const metadata = quote.Metadata && {
    ...quote.Metadata,
    Prices: quote.Metadata.Prices && {
      ...quote.Metadata.Prices,
      TotalPrice: normalizePrice(quote.Metadata.Prices.TotalPrice),
      AmountDue: normalizePrice(quote.Metadata.Prices.AmountDue),
      DealerAccessoriesTotalPrice: normalizePrice(
        quote.Metadata.Prices.DealerAccessoriesTotalPrice
      ),
    },
  }

  return {
    ...quote,
    Metadata: metadata,
    QuoteDetails: normalizeQuoteDetails(quote.QuoteDetails),
    PrecalculativeResultGuid: precalculativeResultGuid(quote.Metadata?.Prices?.TotalPrice),
    DeliveryDates: pascalCaseKeys(quote.DeliveryDates),
    QuoteSendToAutoline: quote.QuoteSendToAutoline ?? false,
    SalesOrderSendToAutoline: quote.SalesOrderSendToAutoline ?? false,
    LinkedChangeOrders: quote.LinkedChangeOrders && [...quote.LinkedChangeOrders].sort(byCreatedOn),
    ChildQuotes:
      quote.ChildQuotes && [...quote.ChildQuotes].sort(byCreatedOn).map(normalizeChildQuote),
    LatestVwpfsCall: normalizeLatestVwpfsCall(quote.LatestVwpfsCall),
  }
}

// The only QuoteDetails fields PCQM types as a plain string. Everything else is an object,
// enum, date, number, boolean or list, so '' is never a valid value for it.
//
// The store seeds several of those with '' (`LeaseReference: ''` in quote.js and in
// CustomerConfigurationPageWrapper.initQuote, `CreatedBySource: ''` in the reset state).
// That used to be absorbed silently: Newtonsoft coerces '' to null when binding to an
// object-typed property, so PCQM only ever saw null. Jackson rejects it outright --
//   Cannot coerce empty String ("") to LeaseReferenceDTO
// -- so the pass-through has to do what the C# layer was quietly doing.
const TEXT_FIELDS = new Set([
  'EmailBodyQuote',
  'EmailBodySalesOrder',
  'Remark',
  'ShoppingCartId',
  'Reason',
])

function blankToNull(details) {
  return Object.fromEntries(
    Object.entries(details).map(([k, v]) => [k, v === '' && !TEXT_FIELDS.has(k) ? null : v])
  )
}

/**
 * The store's QuoteDetails -> the shape PCQM accepts on a request body. Only field names and
 * types are translated here; data rules such as the customer PoId "0" default are PCQM's
 * (CstQuoteWriteService.applyDefaults, CST-4514).
 */
export function denormalizeQuoteDetails(details) {
  if (!details) return details
  const { IsStockOrder, ...rest } = blankToNull(details)
  return {
    ...rest,
    StockOrder: IsStockOrder !== undefined ? IsStockOrder : details.StockOrder,
    Cars: details.Cars?.map(denormalizeCar),
    DealerAccessories: details.DealerAccessories?.map(denormalizeAccessory),
  }
}
